// src/utils/imageUtils.js

/**
 * Utility functions for handling and optimizing images
 */

/**
 * Creates a thumbnail URL using a third-party image resizing service
 * @param {string} originalUrl - The original image URL
 * @param {number} maxWidth - Maximum width for the thumbnail (default: 400px)
 * @param {number} maxHeight - Maximum height for the thumbnail (default: 400px)
 * @param {number} quality - Image quality 1-100 (default: 80)
 * @returns {string} The thumbnail URL
 */
export const createThumbnailUrl = (originalUrl, maxWidth = 400, maxHeight = 400, quality = 80) => {
  if (!originalUrl) return originalUrl;
  
  console.log('createThumbnailUrl called with:', originalUrl);
  
  // Check if it's a Firebase Storage URL
  if (originalUrl.includes('firebasestorage.googleapis.com')) {
    // Use images.weserv.nl for resizing Firebase Storage images
    const optimizedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(originalUrl)}&w=${maxWidth}&h=${maxHeight}&fit=cover&q=${quality}&output=webp`;
    console.log('Created optimized URL:', optimizedUrl);
    return optimizedUrl;
  }
  
  // For Pinterest URLs, use their built-in sizing
  if (originalUrl.includes('pinimg.com')) {
    // Pinterest has different size variants: 236x, 474x, 564x, 736x, originals
    return originalUrl.replace('/originals/', '/564x/').replace('/736x/', '/564x/');
  }
  
  // Return original URL if no optimization is possible
  return originalUrl;
};

/**
 * Predefined image sizes for different use cases
 */
export const ImageSizes = {
  SMALL: { width: 300, height: 300, quality: 80 },      // For small cards/lists
  MEDIUM: { width: 300, height: 300, quality: 80 },     // For grid items
  LARGE: { width: 600, height: 600, quality: 85 },      // For modal/detail views
  BANNER: { width: 400, height: 200, quality: 80 },     // For header banners
  HEADER: { width: 1600, height: 533, quality: 85 },
};

/**
 * Get optimized image URL for specific use case
 * @param {string} originalUrl - The original image URL
 * @param {string} size - Size key from ImageSizes
 * @returns {string} Optimized image URL
 */
export const getOptimizedImageUrl = (originalUrl, size = 'MEDIUM') => {
  console.log('getOptimizedImageUrl called with:', originalUrl, size);
  
  const sizeConfig = ImageSizes[size];
  if (!sizeConfig) return originalUrl;
  
  const result = createThumbnailUrl(
    originalUrl, 
    sizeConfig.width, 
    sizeConfig.height, 
    sizeConfig.quality
  );
  
  console.log('getOptimizedImageUrl returning:', result);
  return result;
};
